import React, { useCallback, useMemo } from 'react';
import AIEduDataTable from '../../../components/DataTable/AIEduDataTable';
import AIEduPaginator from '../../../components/Paginator/AIEduPaginator';
import AIEduSearch from '../../../components/Search/AIEduSearch';
import AIEduButton from '../../../components/Button/AIEduButton';
import NoDataImage from '../../../assets/images/alert_img.svg';
import styles from '../emergencyAlert.module.css';
import { useIntl } from 'react-intl';

const EmergencyAlertList = React.memo(({
    searchTerm,
    setSearchTerm,
    selectedAlertsList,
    setSelectedAlertsList,
    onAddNew,
    onDelete,
    data,
    columns,
    totalRecords,
    first,
    rows,
    onPageChange,
    onSort,
    onSearch,
    sortBy,
    sortOrder,
    handleInputKeyDown,
}) => {
    const intl = useIntl();
    const { formatMessage: f } = intl;

    const handleAlertSearch = useCallback(() => {
        onSearch();
    }, [onSearch]);

    const handleSelectionChange = useCallback((e) => {
        setSelectedAlertsList(e.value);
    }, [setSelectedAlertsList]);

    const sortIcon = (options) => {
        if (options.sortOrder === 1) {
            return (
                <i
                    className='pi pi-sort-up-fill'
                    style={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                />
            );
        }
        if (options.sortOrder === -1) {
            return (
                <i
                    className='pi pi-sort-down-fill'
                    style={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                />
            );
        }
    };

    // Memoize the no data content to prevent unnecessary re-renders
    const noDataContent = useMemo(() => (
        <div>
            <div className={styles.emergency_alert_noDataImg}>
                <img src={NoDataImage} style={{ height: '100%', transform: 'scale(1.2)' }} alt="No data" />
            </div>
            <div className={styles.emergency_alert_noDataDescription}>
                {f({ id: 'AI_EDU_NO_EMERGENCY_ALERTS_DESCRIPTION' })}
            </div>

            <div className='flex justify-content-center mt-4'>
                <AIEduButton
                    label={f({ id: 'AI_EDU_CREATE_NEW_ALERT' })}
                    className='btnPrimary'
                    onClick={onAddNew}
                    style={{ width: '20rem'}}
                />
            </div>
        </div>
    ), [onAddNew]);


    // Show data table if there's data OR if there's a search term (even with no results)
    const shouldShowDataTable = data?.length > 0 || searchTerm?.trim() !== '';

    return shouldShowDataTable ? (
        <>
            <div className='mb-4 flex justify-content-between flex-wrap gap-3'>
                <div className='flex flex-wrap gap-6'>
                    <AIEduSearch
                        value={searchTerm}
                        setValue={setSearchTerm}
                        placeholder={f({ id: 'AI_EDU_SEARCH_ALERT' })}
                        searchHandler={handleAlertSearch}
                        handleInputKeyDown={handleInputKeyDown}
                    />
                </div>
                <div className='flex align-items-center flex-wrap'>
                    <AIEduButton
                        icon='pi pi-trash'
                        className={styles.emergency_alert_delete}
                        onClick={onDelete}
                        disabled={selectedAlertsList.length === 0}
                    />
                    <div className={`${styles.vertical_divider} mx-3`} />
                    <AIEduButton
                        label={f({ id: 'AI_EDU_ADD_NEW' })}
                        icon='pi pi-plus'
                        className='btnPrimary'
                        onClick={onAddNew}
                    />
                </div>
            </div>

            <div
                className='mb-2'
                style={{
                    height: '1rem',
                    fontFamily: 'SamsungOne-700, sans-serif',
                }}
            >
                {selectedAlertsList?.length > 0 && (
                    <span>
                        {selectedAlertsList.length} of {data?.length}{' '}
                        {f({ id: 'AI_EDU_SELECTED_COUNT' })}
                    </span>
                )}
            </div>

            <AIEduDataTable
                data={data}
                columns={columns}
                scrollHeight='60vh'
                onSort={onSort}
                sortIcon={sortIcon}
                sortField={sortBy}
                sortOrder={sortOrder}
                lazy={true}
                dataKey='alertId'
                selectionMode='multiple'
                selection={selectedAlertsList}
                onSelectionChange={handleSelectionChange}
                rowClassName={styles.emergency_alert_row}
                emptyMessage={searchTerm?.trim() !== '' 
                    ? f({ id: 'AI_EDU_NO_SEARCH_RESULTS' })
                    : f({ id: 'AI_EDU_NO_DATA_TO_SHOW' })
                }
            />

           {data?.length > 0 && <AIEduPaginator
                rows={rows}
                first={first}
                totalRecords={totalRecords}
                onPageChange={onPageChange}
            />}
        </>
    ) : noDataContent;
});

EmergencyAlertList.displayName = 'EmergencyAlertList';

export default EmergencyAlertList;
